/**
 * File mapping status calculation utilities
 */

import { ColumnMapping, StudyForm } from '../components/types';
import { getRequiredMetadataTypes } from '../constants/metadataPatterns';

export interface FileMappingStatus {
  status: string;
  score: number;
  details: string;
  metrics: {
    fileColumnCoverage: number;
    requiredFieldCoverage: number;
    unmappedFileColumns: number;
    missingRequiredFields: number;
    unmappedRequiredMetadata: string[];
    hasUnmappedRequiredMetadata: boolean;
  };
}

/**
 * Calculate file mapping status based on form, columns, and mappings
 */
export const calculateFileMappingStatus = (
  selectedForm: StudyForm | null,
  fileColumns: string[],
  columnMappings: ColumnMapping[],
  fileId: string,
  isSelectedFormSubform: boolean
): FileMappingStatus => {
  if (!selectedForm) {
    return {
      status: 'unmapped',
      score: 0,
      details: 'No form selected',
      metrics: {
        fileColumnCoverage: 0,
        requiredFieldCoverage: 0,
        unmappedFileColumns: fileColumns.length,
        missingRequiredFields: 0,
        unmappedRequiredMetadata: [],
        hasUnmappedRequiredMetadata: false,
      },
    };
  }

  if (!selectedForm.fields || selectedForm.fields.length === 0) {
    return {
      status: 'unmapped',
      score: 0,
      details: 'Form has no fields defined',
      metrics: {
        fileColumnCoverage: 0,
        requiredFieldCoverage: 0,
        unmappedFileColumns: fileColumns.length,
        missingRequiredFields: 0,
        unmappedRequiredMetadata: [],
        hasUnmappedRequiredMetadata: false,
      },
    };
  }

  // Get mappings for this file
  const fileMappings = columnMappings.filter(m => m.fileId === fileId);

  const formFields = selectedForm.fields.filter(
    field => field && field.dataPoint
  );
  const requiredFields = formFields.filter(field => field.required);

  // Use the fileMappings already defined above
  const metadataColumns = fileMappings.filter(
    m => m.mappingType === 'metadata'
  );
  const formFieldMappings = fileMappings.filter(
    m => m.mappingType === 'formField'
  );

  // Count all mapped columns (including auto-matches that aren't in columnMappings yet)
  const explicitlyMappedColumns =
    metadataColumns.length + formFieldMappings.length;
  const autoMatchedColumns = getMatchingColumns(
    fileColumns,
    selectedForm
  ).filter(
    col => !fileMappings.some(mapping => mapping.columnName === col)
  ).length;

  const totalMappedColumns = explicitlyMappedColumns + autoMatchedColumns;
  const unmappedFileColumns = fileColumns.length - totalMappedColumns;

  // Check if required fields are covered
  const coveredRequiredFields = requiredFields.filter(
    field =>
      formFieldMappings.some(
        mapping => mapping.targetField === field.dataPoint
      ) ||
      getMatchingColumns(fileColumns, selectedForm).includes(field.dataPoint)
  );
  const missingRequiredFields =
    requiredFields.length - coveredRequiredFields.length;

  // Check if form fields are covered (kept for potential diagnostics)
  const _coveredFormFields = formFields.filter(
    field =>
      formFieldMappings.some(
        mapping => mapping.targetField === field.dataPoint
      ) ||
      getMatchingColumns(fileColumns, selectedForm).includes(field.dataPoint)
  );
  // Check required metadata mapping status
  // mainFormId is required only for subforms, not for main forms
  const requiredMetadataTypes = getRequiredMetadataTypes()
    .filter(meta => meta.type !== 'mainFormId' || isSelectedFormSubform) // mainFormId only for subforms
    .map(meta => meta.type);

  const mappedMetadataTypes = metadataColumns
    .filter(m => m.metadataType?.type)
    .map(m => m.metadataType!.type as string);

  const unmappedRequiredMetadata = requiredMetadataTypes.filter(
    type => !mappedMetadataTypes.includes(type)
  );

  const hasUnmappedRequiredMetadata = unmappedRequiredMetadata.length > 0;

  // Calculate scores
  const fileColumnCoverage = totalMappedColumns / fileColumns.length; // How many file columns are mapped
  const requiredFieldCoverage =
    requiredFields.length > 0
      ? coveredRequiredFields.length / requiredFields.length
      : 1; // Required fields covered

  // Overall score - focus on file completeness and required fields
  // Don't penalize for unused form fields (it's normal for files to contain subset of form data)
  const overallScore =
    fileColumnCoverage * 0.6 + // 60% - file columns mapped (main factor)
    requiredFieldCoverage * 0.4; // 40% - required fields covered (critical)

  // Determine status with file-centric logic
  // Priority: required metadata > required fields > column coverage
  let status: string;
  let details: string;

  if (hasUnmappedRequiredMetadata) {
    status = 'missing-required-metadata';
    details = `Missing required metadata: ${unmappedRequiredMetadata.join(', ')}`;
  } else if (missingRequiredFields > 0) {
    status = 'missing-required';
    details = `Missing ${missingRequiredFields} required field${missingRequiredFields > 1 ? 's' : ''}`;
  } else if (requiredFieldCoverage === 1 && !hasUnmappedRequiredMetadata) {
    // If all required fields and metadata are mapped, consider it excellent
    // Allow some unmapped columns (they might be extra/unused)
    if (fileColumnCoverage >= 0.8) {
      status = 'excellent';
      details = 'All required fields and metadata mapped, ready to import';
    } else if (fileColumnCoverage >= 0.6) {
      status = 'good';
      details =
        unmappedFileColumns > 0
          ? `${unmappedFileColumns} unmapped column${unmappedFileColumns > 1 ? 's' : ''} (not critical)`
          : 'Good mapping coverage';
    } else {
      status = 'partial';
      details = `${unmappedFileColumns} unmapped columns - review mapping`;
    }
  } else if (fileColumnCoverage >= 0.8 && requiredFieldCoverage === 1) {
    status = 'good';
    details =
      unmappedFileColumns > 0
        ? `${unmappedFileColumns} unmapped column${unmappedFileColumns > 1 ? 's' : ''}`
        : 'Good mapping coverage';
  } else if (fileColumnCoverage >= 0.6) {
    status = 'partial';
    details = `${unmappedFileColumns} unmapped columns - review mapping`;
  } else {
    status = 'poor';
    details = `Many unmapped columns - consider different form`;
  }

  return {
    status,
    score: overallScore,
    details,
    metrics: {
      fileColumnCoverage,
      requiredFieldCoverage,
      unmappedFileColumns,
      missingRequiredFields,
      unmappedRequiredMetadata,
      hasUnmappedRequiredMetadata,
    },
  };
};

/**
 * Get columns that match form fields by name
 */
function getMatchingColumns(
  fileColumns: string[],
  selectedForm: StudyForm
): string[] {
  if (!selectedForm || !selectedForm.fields) return [];

  const formFields = selectedForm.fields
    .filter(field => field && field.dataPoint)
    .map(field => field.dataPoint);

  return fileColumns.filter(col =>
    formFields.some(field => field.toLowerCase() === col.toLowerCase())
  );
}
